import { type FC, useEffect, useState } from "react";

import { formatDuration } from "./helpers";

/**
 * Countdown to the next actor activation. Lifted verbatim from the former
 * InfoTab god component. Renders the tick interval while running/killed, a
 * live countdown while waiting, and a "ready" state once the interval elapses.
 */
export const CooldownDisplay: FC<{
  tickIntervalMs: number;
  lastParkedAt: string | null;
  status: string;
}> = ({ tickIntervalMs, lastParkedAt, status }) => {
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  useEffect(() => {
    if (status !== "waiting" || !lastParkedAt) {
      setRemainingMs(null);
      return;
    }
    const tick = () => {
      const elapsed = Date.now() - new Date(lastParkedAt).getTime();
      const remaining = tickIntervalMs - elapsed;
      setRemainingMs(remaining > 0 ? remaining : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tickIntervalMs, lastParkedAt, status]);

  const intervalLabel = formatDuration(tickIntervalMs);

  if (remainingMs === null) {
    return (
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono text-[11px] tabular-nums text-slate-700 dark:text-slate-200">
          {intervalLabel}
        </span>
        <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
          between activations
        </span>
      </div>
    );
  }

  if (remainingMs === 0) {
    return (
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono text-[11px] tabular-nums text-emerald-500">
          ready
        </span>
        <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
          waiting for mailbox
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-baseline gap-1.5">
      <span className="font-mono text-[11px] tabular-nums text-amber-500">
        {formatDuration(remainingMs)}
      </span>
      <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
        until ready ({intervalLabel} interval)
      </span>
    </div>
  );
};
